/**
 * Single Service - Singleton entities stored in single_records
 *
 * Supports:
 * - Read single config
 * - Update config
 * - Defaults on first read
 * - Scoped mode (absorbed from ConfigService): multiple records keyed by scope
 *
 * Uses single_records table with key-value storage:
 * { key: 'site_config', value: { siteName: '...', logo: '...' } }
 *
 * Scoped mode (when scopeField is set):
 * { key: 'mail_config:default', value: { host: '...', port: 587 } }
 * { key: 'mail_config:tenant-123', value: { host: '...', port: 465 } }
 */

import type {
  SingleEntityDefinition,
  PaginatedResult,
  ModuleContext,
  EntityServiceHooks
} from '@gzl10/nexus-sdk'
import type { EntityQuery } from '@kernel/runtime/index.js'
import { BaseEntityService } from './base.service.js'
import { resolveEnvMapping, getEnvLockedFields, stripEnvLockedFields } from '../helpers/env-mapping.js'
import type { EntityRepository, ISingleRepository } from '../repositories/types.js'
import { KnexSingleKvRepository } from '../repositories/knex/single-kv.repo.js'

const SINGLE_RECORDS_TABLE = 'single_records'

/** Strip framework-internal fields (_authUserId, etc.) before persisting */
function stripInternalFields<T extends Record<string, unknown>>(data: T): T {
  const clean = { ...data }
  for (const key of Object.keys(clean)) {
    if (key.startsWith('_')) delete clean[key]
  }
  return clean
}

export class SingleService<T extends Record<string, unknown> = Record<string, unknown>>
  extends BaseEntityService<T> {

  declare readonly definition: SingleEntityDefinition

  constructor(ctx: ModuleContext, definition: SingleEntityDefinition, hooks?: EntityServiceHooks<T>, repo?: EntityRepository) {
    super(ctx, definition, hooks, repo)
  }

  /**
   * Build the KV repository for this single entity.
   * SingleService always stores in single_records (KV), so we override buildRepo
   * to provide a KnexSingleKvRepository instead of the default factory output.
   */
  protected override buildRepo(): EntityRepository {
    return new KnexSingleKvRepository(this.db, {
      entityKey: this.key,
      scopeField: this.definition.scopeField,
      idGenerator: this.generateId,
      timestampFn: () => this.getNowTimestamp(),
    }) as unknown as EntityRepository
  }

  /**
   * Get table - overridden since single entities use single_records
   */
  protected override get table(): string {
    return SINGLE_RECORDS_TABLE
  }

  /**
   * Get the setting key
   */
  private get key(): string {
    return this.definition.key
  }

  /**
   * Whether this entity operates in scoped mode (multiple records)
   */
  private get isScoped(): boolean {
    return !!this.definition.scopeField
  }

  /**
   * Build the full key for a scope value
   */
  private scopedKey(scopeValue: string): string {
    return `${this.key}:${scopeValue}`
  }

  // ── findAll ──────────────────────────────────────────────────────────

  /**
   * Get all - returns single item in paginated format (singleton mode)
   * or all scoped records (scoped mode)
   */
  async findAll(_query?: EntityQuery): Promise<PaginatedResult<T>> {
    if (this.isScoped) {
      return this.findAllScoped()
    }

    const value = await this.findById(this.key)

    return {
      items: value ? [value] : [],
      total: value ? 1 : 0,
      page: 1,
      limit: 1,
      totalPages: value ? 1 : 0,
      hasNext: false
    }
  }

  /**
   * Get all scoped records
   */
  private async findAllScoped(): Promise<PaginatedResult<T>> {
    // LIMITATION: ISingleRepository has no bulk-list-by-prefix operation; direct KV scan required.
    const prefix = `${this.key}:`
    const rows = await this.db(SINGLE_RECORDS_TABLE)
      .where('key', 'like', `${prefix}%`)
      .orderBy('created_at', 'asc') as Array<{ id: string; key: string; value: string; created_at: string; updated_at: string }>

    const items: T[] = []
    for (const row of rows) {
      try {
        const value = this.parseValue(row.value)
        const scopeValue = row.key.slice(prefix.length)
        const merged = this.mergeWithDefaults({
          ...value,
          id: row.id,
          [this.definition.scopeField!]: scopeValue
        })
        items.push(merged as T)
      } catch {
        this.logger.warn({ key: row.key }, 'Failed to parse scoped setting value')
      }
    }

    return {
      items,
      total: items.length,
      page: 1,
      limit: items.length || 1,
      totalPages: 1,
      hasNext: false
    }
  }

  // ── findById ─────────────────────────────────────────────────────────

  /**
   * Get the singleton value (singleton mode)
   * or get a scoped record by ID (scoped mode)
   */
  async findById(id: string): Promise<T | null> {
    if (this.isScoped) {
      return this.findByIdScoped(id)
    }

    // Delegate raw fetch to repository; service handles JSON parsing, defaults, envMapping
    const repo = this.repo as ISingleRepository<T>
    let raw: T | null
    try {
      raw = await repo.get()
    } catch {
      this.logger.warn({ key: this.key }, 'Failed to parse setting value')
      return this.applyEnvMapping(this.definition.defaults as T ?? null as unknown as T)
    }

    if (!raw) {
      if (this.definition.defaults) {
        return this.applyEnvMapping(this.definition.defaults as T)
      }
      return null
    }

    const merged = this.mergeWithDefaults(raw as unknown as Record<string, unknown>)
    const withEnv = this.applyEnvMapping(merged as T)
    return this.processAfterFindById(withEnv)
  }

  /**
   * Find a scoped record by its DB id
   */
  private async findByIdScoped(id: string): Promise<T | null> {
    // LIMITATION: ISingleRepository.get(scope) works by scope value, not by row id.
    // Lookup by DB id requires a direct query to resolve the composite key first.
    const row = await this.db(SINGLE_RECORDS_TABLE)
      .where('id', id)
      .first<{ id: string; key: string; value: string }>()

    if (!row || !row.key.startsWith(`${this.key}:`)) {
      return null
    }

    try {
      const value = this.parseValue(row.value)
      const scopeValue = row.key.slice(`${this.key}:`.length)
      const merged = this.mergeWithDefaults({
        ...value,
        id: row.id,
        [this.definition.scopeField!]: scopeValue
      })
      return this.processAfterFindById(merged as T)
    } catch {
      this.logger.warn({ id, key: this.key }, 'Failed to parse scoped setting value')
      return null
    }
  }

  /**
   * Get the setting - alias for findById
   */
  async get(): Promise<T | null> {
    return this.findById(this.key)
  }

  // ── Scoped methods (absorbed from ConfigService) ─────────────────────

  /**
   * Get config by scope value
   */
  async findByScope(scopeValue: string): Promise<T | null> {
    // LIMITATION: ISingleRepository.get(scope) returns raw T without mergeWithDefaults/envMapping.
    // findByScope applies those layers; it cannot be fully replaced by repo.get() without
    // introducing service logic into the repo.
    const row = await this.db(SINGLE_RECORDS_TABLE)
      .where('key', this.scopedKey(scopeValue))
      .first<{ id: string; key: string; value: string }>()

    if (!row) {
      if (this.definition.defaults) {
        return this.definition.defaults as T
      }
      return null
    }

    try {
      const value = this.parseValue(row.value)
      return this.mergeWithDefaults({
        ...value,
        id: row.id,
        [this.definition.scopeField!]: scopeValue
      }) as T
    } catch {
      this.logger.warn({ key: this.scopedKey(scopeValue) }, 'Failed to parse scoped setting value')
      return this.definition.defaults as T ?? null
    }
  }

  /**
   * Update or create config by scope
   */
  async upsertByScope(scopeValue: string, data: Partial<T>): Promise<T> {
    // LIMITATION: upsertByScope needs beforeCreate/beforeUpdate hooks with the existing id,
    // and falls back to findByScope (which applies defaults/envMapping). The ISingleRepository
    // upsert interface cannot carry these lifecycle concerns without leaking service logic.
    const fullKey = this.scopedKey(scopeValue)
    const existing = await this.db(SINGLE_RECORDS_TABLE)
      .where('key', fullKey)
      .first<{ id: string; key: string; value: string }>()

    const now = this.getNowTimestamp()

    if (existing) {
      // Merge with current value
      let currentValue: Record<string, unknown> = {}
      try {
        currentValue = this.parseValue(existing.value)
      } catch { /* empty */ }

      const newValue = { ...currentValue, ...data }
      // Remove internal fields
      delete (newValue as Record<string, unknown>)[this.definition.scopeField!]
      delete (newValue as Record<string, unknown>)['id']

      const processedData = await this.beforeUpdate(existing.id, newValue as Partial<T>)

      await this.db(SINGLE_RECORDS_TABLE)
        .where('key', fullKey)
        .update({
          value: JSON.stringify(processedData),
          updated_at: now
        })

      const result = await this.findByScope(scopeValue)
      if (result) await this.afterUpdate(result)
      return result!
    } else {
      // Create new scoped record
      const entityData = { ...this.definition.defaults, ...data }
      // Remove scope field and id from stored value
      delete (entityData as Record<string, unknown>)[this.definition.scopeField!]
      delete (entityData as Record<string, unknown>)['id']

      const processedData = await this.beforeCreate(entityData as Partial<T>)
      const id = this.generateId()

      await this.db(SINGLE_RECORDS_TABLE).insert({
        id,
        key: fullKey,
        value: JSON.stringify(processedData),
        created_at: now,
        updated_at: now
      })

      const result = await this.findByScope(scopeValue)
      if (result) await this.afterCreate(result)
      return result!
    }
  }

  /**
   * Set a scoped config as the default
   */
  async setAsDefault(scopeValue: string): Promise<void> {
    // LIMITATION: meta-key (`__default` marker) is a KV convention internal to the service;
    // ISingleRepository has no concept of a "default pointer" record.
    const metaKey = `${this.key}:__default`
    const now = this.getNowTimestamp()
    const existing = await this.db(SINGLE_RECORDS_TABLE)
      .where('key', metaKey)
      .first()

    if (existing) {
      await this.db(SINGLE_RECORDS_TABLE)
        .where('key', metaKey)
        .update({ value: JSON.stringify(scopeValue), updated_at: now })
    } else {
      await this.db(SINGLE_RECORDS_TABLE).insert({
        id: this.generateId(),
        key: metaKey,
        value: JSON.stringify(scopeValue),
        created_at: now,
        updated_at: now
      })
    }
  }

  /**
   * Get the default scoped config
   */
  async getDefault(): Promise<T | null> {
    // LIMITATION: resolving the __default meta-key and prefix-scanning require direct KV queries
    // that have no equivalent in ISingleRepository.
    const metaKey = `${this.key}:__default`
    const meta = await this.db(SINGLE_RECORDS_TABLE)
      .where('key', metaKey)
      .first<{ value: string }>()

    if (meta) {
      try {
        const defaultScope = typeof meta.value === 'string' ? JSON.parse(meta.value) : meta.value
        return this.findByScope(defaultScope)
      } catch { /* fall through */ }
    }

    // Fallback: return first scoped record
    const prefix = `${this.key}:`
    const first = await this.db(SINGLE_RECORDS_TABLE)
      .where('key', 'like', `${prefix}%`)
      .whereNot('key', metaKey)
      .orderBy('created_at', 'asc')
      .first<{ id: string; key: string; value: string }>()

    if (first) {
      const scopeValue = first.key.slice(prefix.length)
      return this.findByScope(scopeValue)
    }

    // No records: return defaults
    if (this.definition.defaults) {
      return this.definition.defaults as T
    }

    return null
  }

  // ── update ───────────────────────────────────────────────────────────

  /**
   * Update the singleton value (singleton mode)
   * or update a scoped record by ID (scoped mode)
   */
  async update(id: string, data: Partial<T>): Promise<T> {
    if (this.isScoped) {
      return this.updateScoped(id, data)
    }

    // Strip env-locked fields from incoming data (silent — env vars win)
    const safeData = this.definition.envMapping
      ? stripEnvLockedFields(data as Record<string, unknown>, this.definition.envMapping) as Partial<T>
      : data

    // Strip framework-internal fields before hook/persist
    const cleanData = stripInternalFields(safeData as Record<string, unknown>) as Partial<T>

    // Run beforeUpdate hook
    const processedData = await this.beforeUpdate(this.key, cleanData)

    // Delegate upsert to repository (KV-aware); repo handles merge with existing
    const repo = this.repo as ISingleRepository<T>
    await repo.upsert(processedData)

    const result = await this.get()
    if (!result) {
      throw new this.errors.AppError('Failed to save setting', 500)
    }

    // Run afterUpdate hook
    await this.afterUpdate(result)

    return result
  }

  /**
   * Update a scoped record by its DB id
   */
  private async updateScoped(id: string, data: Partial<T>): Promise<T> {
    // LIMITATION: ISingleRepository.upsert(data, scope) works by scope value, not row id.
    // Resolving scope from a DB id requires a direct lookup before delegating.
    const row = await this.db(SINGLE_RECORDS_TABLE)
      .where('id', id)
      .first<{ id: string; key: string; value: string }>()

    if (!row || !row.key.startsWith(`${this.key}:`)) {
      throw new this.errors.NotFoundError('Config not found')
    }

    let currentValue: Record<string, unknown> = {}
    try {
      currentValue = this.parseValue(row.value)
    } catch { /* empty */ }

    const newValue = stripInternalFields({ ...currentValue, ...data })
    // Remove scope/id fields from stored value
    delete (newValue as Record<string, unknown>)[this.definition.scopeField!]
    delete (newValue as Record<string, unknown>)['id']

    const processedData = await this.beforeUpdate(id, newValue as Partial<T>)
    const now = this.getNowTimestamp()

    await this.db(SINGLE_RECORDS_TABLE)
      .where('id', id)
      .update({
        value: JSON.stringify(processedData),
        updated_at: now
      })

    const scopeValue = row.key.slice(`${this.key}:`.length)
    const result = await this.findByScope(scopeValue)
    if (!result) {
      throw new this.errors.AppError('Config not found after update', 404)
    }

    await this.afterUpdate(result)
    return result
  }

  /**
   * Set the setting - alias for update
   */
  async set(data: Partial<T>): Promise<T> {
    return this.update(this.key, data)
  }

  /**
   * Reset to defaults
   */
  async reset(): Promise<T | null> {
    if (!this.definition.defaults) {
      // LIMITATION: ISingleRepository has no delete operation; direct KV delete required.
      await this.db(SINGLE_RECORDS_TABLE)
        .where('key', this.key)
        .delete()
      return null
    }

    // Set to defaults
    return this.update(this.key, this.definition.defaults as Partial<T>)
  }

  /**
   * Seed defaults into the database if record doesn't exist.
   * This ensures endpoints return persisted data instead of in-memory defaults.
   *
   * @returns 1 if seeded, 0 if already exists or no defaults
   */
  async seed(): Promise<number> {
    // No defaults to seed
    if (!this.definition.defaults) {
      return 0
    }

    // LIMITATION: ISingleRepository has no existence-check-then-insert-only operation.
    // Seed is a one-shot initialization; direct KV insert avoids the merge logic of upsert.
    const exists = await this.db(SINGLE_RECORDS_TABLE)
      .where('key', this.key)
      .first()

    if (exists) {
      return 0
    }

    // Insert defaults
    const now = this.getNowTimestamp()
    await this.db(SINGLE_RECORDS_TABLE).insert({
      id: this.generateId(),
      key: this.key,
      value: JSON.stringify(this.definition.defaults),
      created_at: now,
      updated_at: now
    })

    this.logger.info({ key: this.key }, 'Single entity defaults seeded')
    return 1
  }

  // ── Helpers ──────────────────────────────────────────────────────────

  /**
   * Parse the value column — handles both string (text column) and
   * pre-parsed object (json/jsonb column where pg driver auto-deserializes).
   */
  private parseValue(raw: unknown): Record<string, unknown> {
    if (typeof raw === 'object' && raw !== null) return raw as Record<string, unknown>
    if (typeof raw === 'string') return JSON.parse(raw) as Record<string, unknown>
    throw new Error('Unexpected value type')
  }

  /**
   * Merge entity with defaults
   */
  private mergeWithDefaults(entity: Record<string, unknown>): Record<string, unknown> {
    if (!this.definition.defaults) {
      return entity
    }
    return { ...this.definition.defaults, ...entity }
  }

  /**
   * Applies envMapping overrides and appends _envLocked to the result.
   * No-op if envMapping is not defined or no env vars are set.
   */
  private applyEnvMapping(data: T): T {
    if (!this.definition.envMapping || !data) return data
    const locked = getEnvLockedFields(this.definition.envMapping)
    if (locked.length === 0) return data
    const overridden = resolveEnvMapping(data as Record<string, unknown>, this.definition.envMapping)
    return { ...overridden, _envLocked: locked } as unknown as T
  }
}
